/* LL 126SE: R0 gift-product sauces, reconciled only through SE's cart mutation lane. */
(function (scope, factory) {
  const api = factory();
  if (typeof module === 'object' && module.exports) module.exports = api;
  else {
    scope.LLFreeSauces = api;
    if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', api.mount, { once: true });
    else api.mount();
  }
})(typeof window === 'undefined' ? globalThis : window, function () {
  const propertyName = '_ll_free_sauce';
  const optOutStorageKey = 'll-free-sauce-optouts';
  const notFreeLineText = 'Not free with this basket yet';
  const basketIssueText = "Your free sauces aren't applying to this basket. Please refresh. If it stays, remove the free sauces or contact us.";
  const soldOutText = 'Free sauces are sold out right now.';
  const updatingText = 'Updating your free sauces...';
  const memoryOptOuts = new Set();
  let checkoutState = {
    active: false,
    pending: false,
    running: false,
    blocked: false,
    paid: false,
    soldOut: false,
    complete: true,
    message: '',
    reason: ''
  };

  function ordinalOf(item) {
    const value = Number(item?.properties?.[propertyName]);
    return Number.isInteger(value) && value > 0 ? value : 0;
  }

  function isActive(config, now = Date.now()) {
    if (!config?.enabled || config.active === false) return false;
    const end = Date.parse(config.ends || '');
    return !Number.isFinite(end) || now <= end;
  }

  function targetCount(config, now = Date.now()) {
    return isActive(config, now) ? Math.max(0, Math.floor(Number(config.meals || 0) / 15)) : 0;
  }

  function normalizeOptOuts(optOuts) {
    const values = optOuts instanceof Set ? [...optOuts] : Array.isArray(optOuts) ? optOuts : [];
    return new Set(values.map(Number).filter(value => Number.isInteger(value) && value > 0));
  }

  function expectedOrdinals(config, optOuts, now = Date.now()) {
    const optedOut = normalizeOptOuts(optOuts);
    return Array.from({ length: targetCount(config, now) }, (_, index) => index + 1).filter(ordinal => !optedOut.has(ordinal));
  }

  function freeLines(cart) {
    return (cart?.items || []).filter(item => ordinalOf(item));
  }

  function variantIdOf(item) {
    return Number(item?.variant_id || item?.id) || 0;
  }

  function linePresentation(item) {
    const marked = ordinalOf(item) > 0;
    const free = marked && Number(item?.final_line_price) === 0;
    return { marked, free, label: free ? 'FREE' : '', note: marked && !free ? notFreeLineText : '' };
  }

  function availableVariants(config) {
    return (config?.variants || []).filter(variant => variant.available && Number(variant.id) > 0);
  }

  function isGiftProductLine(item, config) {
    const productId = Number(config?.productId) || 0;
    const variantIds = new Set((config?.variants || []).map(variant => Number(variant.id)).filter(Boolean));
    return (productId > 0 && Number(item?.product_id) === productId) || variantIds.has(variantIdOf(item));
  }

  function defaultVariant(config, ordinal) {
    const variants = availableVariants(config);
    return variants.length ? variants[(ordinal - 1) % variants.length] : null;
  }

  function evaluateGuard(cart, config, optOuts, phase = {}, now = Date.now()) {
    const active = isActive(config, now);
    const lines = freeLines(cart);
    const rogue = (cart?.items || []).filter(item => isGiftProductLine(item, config) && !ordinalOf(item));
    const expected = expectedOrdinals(config, optOuts, now);
    const actual = lines.map(ordinalOf).sort((a, b) => a - b);
    const missing = expected.filter(ordinal => !actual.includes(ordinal));
    const unavailable = active && availableVariants(config).length === 0 ? missing : [];
    const required = expected.filter(ordinal => !unavailable.includes(ordinal));
    const availableIds = new Set(availableVariants(config).map(variant => Number(variant.id)));
    const variantsValid = !active || lines.every(item => availableIds.has(variantIdOf(item)));
    const exact = rogue.length === 0 && actual.length === required.length && actual.every((ordinal, index) => ordinal === required[index]) && lines.every(item => Number(item.quantity) === 1) && variantsValid;
    const pricesConfirmed = lines.every(item => Number(item.final_line_price) === 0);
    const paid = lines.some(item => Number(item.final_line_price) > 0);
    const hasWork = expected.length > 0 || lines.length > 0 || rogue.length > 0;
    const pending = hasWork && phase.pending === true;
    const running = hasWork && phase.running === true;
    const complete = exact && pricesConfirmed;
    const soldOut = unavailable.length > 0;
    const blocked = paid || pending || running || rogue.length > 0 || (active && hasWork && !complete && !soldOut);
    return {
      active,
      pending,
      running,
      blocked,
      paid,
      soldOut,
      complete,
      expected,
      required,
      actual,
      unavailable,
      rogue,
      message: paid ? basketIssueText : (soldOut ? String(config?.soldOutText || soldOutText) : ''),
      reason: blocked ? (paid ? basketIssueText : updatingText) : ''
    };
  }

  function planNext(cart, config, now = Date.now(), optOuts = []) {
    const target = targetCount(config, now);
    const optedOut = normalizeOptOuts(optOuts);
    const rogue = (cart?.items || []).find(item => isGiftProductLine(item, config) && !ordinalOf(item));
    if (rogue) return { type: 'remove', key: rogue.key, ordinal: 0, abuseGuard: true };
    const lines = (cart?.items || []).map((item, index) => ({ item, index, ordinal: ordinalOf(item) })).filter(line => line.ordinal);
    const surplus = lines
      .filter(line => line.ordinal > target || optedOut.has(line.ordinal))
      .sort((a, b) => b.ordinal - a.ordinal || b.index - a.index);
    if (surplus.length) return { type: 'remove', key: surplus[0].item.key, ordinal: surplus[0].ordinal };

    const occupied = new Map();
    for (const line of lines) {
      if (!occupied.has(line.ordinal)) occupied.set(line.ordinal, line);
      else return { type: 'remove', key: line.item.key, ordinal: line.ordinal };
    }

    for (const line of occupied.values()) {
      if (Number(line.item.quantity) !== 1) return { type: 'change', key: line.item.key, quantity: 1, ordinal: line.ordinal };
    }

    const availableIds = new Set(availableVariants(config).map(variant => Number(variant.id)));
    for (const line of occupied.values()) {
      if (!availableIds.has(variantIdOf(line.item))) {
        return { type: 'remove', key: line.item.key, ordinal: line.ordinal, unavailableVariant: true };
      }
    }

    for (let ordinal = 1; ordinal <= target; ordinal += 1) {
      if (occupied.has(ordinal) || optedOut.has(ordinal)) continue;
      const variant = defaultVariant(config, ordinal);
      if (!variant) return { type: 'unavailable', ordinal };
      return { type: 'add', ordinal, variantId: Number(variant.id) };
    }
    return null;
  }

  function createFetchAdapter(options = {}) {
    const request = options.request;
    if (typeof request !== 'function') throw new Error('Free sauces require the cart mutation queue');
    return {
      getCart: () => request('cart.js'),
      changeLine: (key, quantity) => request('cart/change.js', { id: key, quantity }),
      addLine: (variantId, ordinal) => request('cart/add.js', {
        items: [{ id: Number(variantId), quantity: 1, properties: { [propertyName]: String(ordinal) } }]
      }),
      refresh: options.refresh || (() => Promise.resolve())
    };
  }

  async function reconcile(adapter, config, now = Date.now(), forceRefresh = false, optOuts = []) {
    let cart = await adapter.getCart();
    let changed = false;
    const actions = [];
    for (let guard = 0; guard < 200; guard += 1) {
      const action = planNext(cart, config, now, optOuts);
      if (!action || action.type === 'unavailable') {
        const confirmedCart = await adapter.getCart();
        if (changed || forceRefresh) await adapter.refresh();
        const confirmation = evaluateGuard(confirmedCart, config, optOuts, {}, now);
        return {
          changed,
          blocked: confirmation.blocked,
          actions,
          cart: confirmedCart,
          unavailable: confirmation.unavailable,
          confirmation
        };
      }
      actions.push(action);
      if (action.type === 'remove' || action.type === 'change') {
        cart = await adapter.changeLine(action.key, action.type === 'remove' ? 0 : action.quantity);
      } else {
        await adapter.addLine(action.variantId, action.ordinal);
        cart = await adapter.getCart();
      }
      changed = true;
    }
    throw new Error('Free sauce reconciliation loop guard reached');
  }

  async function swap(adapter, config, selection) {
    const variantId = Number(selection.variantId);
    const ordinal = Number(selection.ordinal);
    const allowed = (config.variants || []).some(variant => Number(variant.id) === variantId && variant.available);
    if (!allowed || !ordinal) throw new Error('Selected sauce flavour is unavailable');
    const cart = await adapter.getCart();
    const line = (cart.items || []).find(item => item.key === selection.key && ordinalOf(item) === ordinal);
    if (!line || Number(line.variant_id || line.id) === variantId) return false;
    const oldVariantId = Number(line.variant_id || line.id);
    await adapter.changeLine(line.key, 0);
    try {
      await adapter.addLine(variantId, ordinal);
    } catch (error) {
      try { await adapter.addLine(oldVariantId, ordinal); } catch (_) { /* Reconciliation will surface the missing line. */ }
      throw error;
    }
    await adapter.refresh();
    return true;
  }

  function getCheckoutState() {
    return { ...checkoutState };
  }

  function stateChanged(next) {
    const keys = ['active', 'pending', 'running', 'blocked', 'paid', 'soldOut', 'complete', 'message', 'reason'];
    return keys.some(key => checkoutState[key] !== next[key]);
  }

  function publishCheckoutState(next) {
    const merged = { ...checkoutState, ...next };
    if (!stateChanged(merged)) return getCheckoutState();
    checkoutState = merged;
    if (typeof document !== 'undefined') {
      document.dispatchEvent(new CustomEvent('ll:free-sauce:state', { detail: getCheckoutState() }));
    }
    return getCheckoutState();
  }

  function readOptOuts() {
    try {
      const stored = JSON.parse(sessionStorage.getItem(optOutStorageKey) || '[]');
      normalizeOptOuts(stored).forEach(ordinal => memoryOptOuts.add(ordinal));
    } catch (_) { /* The in-memory set is the session fallback. */ }
    return new Set(memoryOptOuts);
  }

  function writeOptOuts(optOuts) {
    memoryOptOuts.clear();
    normalizeOptOuts(optOuts).forEach(ordinal => memoryOptOuts.add(ordinal));
    try { sessionStorage.setItem(optOutStorageKey, JSON.stringify([...memoryOptOuts].sort((a, b) => a - b))); } catch (_) { /* In-memory fallback retained. */ }
    return new Set(memoryOptOuts);
  }

  function setDisabled(node, value) {
    if (node && node.disabled !== value) node.disabled = value;
  }

  function setText(node, value) {
    if (node && node.textContent !== value) node.textContent = value;
  }

  function setAttribute(node, name, value) {
    if (node && node.getAttribute(name) !== value) node.setAttribute(name, value);
  }

  function mount() {
    if (typeof document === 'undefined' || document.documentElement.dataset.llFreeSaucesReady) return;
    document.documentElement.dataset.llFreeSaucesReady = 'true';
    let timer;
    let endTimer;
    let running = false;
    let enqueued = false;
    let queued = false;
    let queuedRefresh = false;

    function config() {
      const openDrawer = document.querySelector('#site-cart-sidebar.ll-cart.sidebar--opened [data-ll-free-sauce-config]');
      const node = openDrawer || document.querySelector('[data-ll-cart-page] [data-ll-free-sauce-config], #site-cart-sidebar.ll-cart [data-ll-free-sauce-config]');
      if (!node) return null;
      try { return JSON.parse(node.textContent); } catch (_) { return null; }
    }

    function mutationLane() {
      return window.LLCartMutations && typeof window.LLCartMutations.enqueue === 'function' ? window.LLCartMutations : null;
    }

    function paidLineInDom() {
      return !!document.querySelector('[data-ll-free-sauce-paid]:not([hidden])');
    }

    function rogueGiftLineInDom(current) {
      const productId = Number(current?.productId) || 0;
      return productId > 0 && !!document.querySelector(`[data-js-cart-item][data-product-id="${productId}"]:not([data-ll-free-sauce])`);
    }

    function setControlsDisabled(disabled) {
      document.querySelectorAll('[data-ll-free-sauce-select]').forEach(select => setDisabled(select, disabled));
    }

    function publishWorkingState(current, phase) {
      const active = isActive(current);
      const expected = current ? expectedOrdinals(current, readOptOuts()) : [];
      const hasLines = !!document.querySelector('[data-ll-free-sauce]');
      const rogue = rogueGiftLineInDom(current);
      const hasWork = expected.length > 0 || hasLines || rogue;
      const paid = paidLineInDom();
      const pending = phase === 'pending' && hasWork;
      const isRunning = phase === 'running' && hasWork;
      return publishCheckoutState({
        active,
        pending,
        running: isRunning,
        blocked: paid || rogue || pending || isRunning,
        paid,
        complete: !hasWork,
        message: paid ? basketIssueText : '',
        reason: paid ? basketIssueText : ((rogue || pending || isRunning) ? updatingText : '')
      });
    }

    function renderOptOuts(current = config()) {
      const optedOut = readOptOuts();
      const target = targetCount(current);
      const visible = isActive(current) ? [...optedOut].filter(ordinal => ordinal <= target).sort((a, b) => a - b) : [];
      const signature = visible.join(',');
      document.querySelectorAll('[data-ll-free-sauce-optouts]').forEach(host => {
        if (host.dataset.llFreeSauceOptouts === signature) return;
        host.dataset.llFreeSauceOptouts = signature;
        const fragment = document.createDocumentFragment();
        for (const ordinal of visible) {
          const notice = document.createElement('div');
          notice.className = 'll-cart-free-sauce-promo';
          const copy = document.createElement('span');
          setText(copy, 'Free sauce removed. ');
          const button = document.createElement('button');
          button.type = 'button';
          button.className = 'll-cart-pill';
          button.dataset.llFreeSauceAddBack = String(ordinal);
          setText(button, 'Add it back');
          setAttribute(button, 'aria-label', `Add free sauce ${ordinal} back`);
          notice.append(copy, button);
          fragment.append(notice);
        }
        host.replaceChildren(fragment);
      });
    }

    function markPending(current = config()) {
      setControlsDisabled(true);
      return publishWorkingState(current, 'pending');
    }

    function setEndTimer(current) {
      clearTimeout(endTimer);
      const end = Date.parse(current?.ends || '');
      if (!current?.enabled || !Number.isFinite(end)) return;
      const delay = end - Date.now();
      if (delay > 0 && delay < 2147483647) endTimer = setTimeout(() => run(true), delay + 50);
    }

    function failedState(current) {
      const expected = current ? expectedOrdinals(current, readOptOuts()) : [];
      const hasLines = !!document.querySelector('[data-ll-free-sauce]');
      const rogue = rogueGiftLineInDom(current);
      const paid = paidLineInDom();
      const blocked = paid || rogue || expected.length > 0 || hasLines;
      return {
        active: isActive(current),
        pending: false,
        running: false,
        blocked,
        paid,
        complete: !blocked,
        message: blocked ? basketIssueText : '',
        reason: blocked ? basketIssueText : ''
      };
    }

    async function enqueueWork(worker, forceRefresh = false) {
      if (running || enqueued) {
        queued = true;
        queuedRefresh = queuedRefresh || forceRefresh;
        return;
      }
      const current = config();
      if (!current) {
        publishWorkingState(null, '');
        setControlsDisabled(false);
        return;
      }
      const lane = mutationLane();
      if (!lane) {
        queued = true;
        queuedRefresh = queuedRefresh || forceRefresh;
        markPending(current);
        return;
      }
      enqueued = true;
      markPending(current);
      try {
        await lane.enqueue(async api => {
          enqueued = false;
          running = true;
          const latest = config() || current;
          publishWorkingState(latest, 'running');
          setControlsDisabled(true);
          const adapter = createFetchAdapter({ request: api.request, refresh: api.refresh });
          const result = await worker(adapter, latest);
          if (paidLineInDom() !== result.confirmation.paid && !result.changed) {
            await api.refresh();
          }
          publishCheckoutState(result.confirmation);
          return result;
        }, { type: 'free-sauce' });
      } catch (error) {
        publishCheckoutState(failedState(config() || current));
        console.warn('free-sauces: reconcile', error);
      } finally {
        running = false;
        enqueued = false;
        renderOptOuts(config());
        setEndTimer(config());
        if (queued) {
          const refresh = queuedRefresh;
          queued = false;
          queuedRefresh = false;
          queueMicrotask(() => run(refresh));
        } else {
          setControlsDisabled(false);
        }
      }
    }

    function run(forceRefresh = false) {
      return enqueueWork((adapter, current) => reconcile(adapter, current, Date.now(), forceRefresh, readOptOuts()), forceRefresh);
    }

    function schedule() {
      markPending();
      renderOptOuts();
      clearTimeout(timer);
      timer = setTimeout(() => run(), 120);
    }

    document.addEventListener('ll:cart:rendered', schedule);
    document.addEventListener('ll:free-sauce:refresh', schedule);
    document.addEventListener('ll:cart:mutation-queue-ready', schedule);
    document.addEventListener('add-to-cart', schedule);
    document.addEventListener('krown:cart:add', schedule);
    document.addEventListener('ll:free-sauce:remove-request', event => {
      const ordinal = Number(event.detail?.ordinal);
      if (!ordinal) return;
      const optedOut = readOptOuts();
      optedOut.add(ordinal);
      writeOptOuts(optedOut);
      markPending();
      renderOptOuts();
      run(true);
    });
    document.addEventListener('click', event => {
      const addBack = event.target.closest('[data-ll-free-sauce-add-back]');
      if (!addBack) return;
      event.preventDefault();
      const ordinal = Number(addBack.dataset.llFreeSauceAddBack);
      const optedOut = readOptOuts();
      optedOut.delete(ordinal);
      writeOptOuts(optedOut);
      markPending();
      renderOptOuts();
      run(true);
    }, true);
    document.addEventListener('change', event => {
      const select = event.target.closest('[data-ll-free-sauce-select]');
      if (!select || running || enqueued) return;
      const previous = select.dataset.previous || select.querySelector('[selected]')?.value;
      const current = config();
      markPending(current);
      enqueueWork(async (adapter, latest) => {
        try {
          await swap(adapter, latest, {
            key: select.dataset.lineKey,
            ordinal: select.dataset.ordinal,
            variantId: select.value
          });
          return reconcile(adapter, config() || latest, Date.now(), false, readOptOuts());
        } catch (error) {
          if (previous && select.value !== previous) select.value = previous;
          throw error;
        }
      });
    });
    document.addEventListener('focusin', event => {
      const select = event.target.closest('[data-ll-free-sauce-select]');
      if (select && select.dataset.previous !== select.value) select.dataset.previous = select.value;
    });

    renderOptOuts();
    setEndTimer(config());
    schedule();
  }

  return {
    propertyName,
    optOutStorageKey,
    notFreeLineText,
    basketIssueText,
    soldOutText,
    updatingText,
    ordinalOf,
    isActive,
    targetCount,
    expectedOrdinals,
    variantIdOf,
    linePresentation,
    evaluateGuard,
    availableVariants,
    isGiftProductLine,
    defaultVariant,
    planNext,
    createFetchAdapter,
    reconcile,
    swap,
    readOptOuts,
    writeOptOuts,
    getCheckoutState,
    mount
  };
});
